import {Button, Divider, IconButton, Paper, Typography} from '@yandex-delivery/blocks'
import {Image} from '@yandex-delivery/blocks/image'
import {useI18n} from '@yandex-delivery/core/i18n'
import {isFilled} from '@yandex-delivery/utils'
import clsx from 'clsx'
import {forwardRef, useCallback, useMemo, useState} from 'react'
import type {DropResult} from 'react-beautiful-dnd'
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd'
import {useFormContext} from 'react-hook-form'

import {tagToUrl} from '../../../../lib/tagToUrl'
import {DraggableRouteItem} from '../DraggableRouteItem/DraggableRouteItem'
import * as styles from './RouteInfo.classes'
import type {AddressInfoProps} from './RouteInfo.types'
import {useRemovePointHandlers} from './useRemovePointHandlers'

// тег кнопки закрытия
const CROSS_SLUG = 'delivery_form_cross_fill'

const reorder = (list: string[], startIndex: number, endIndex?: number) => {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)

  if (removed) {
    result.splice(endIndex ?? startIndex, 0, removed)
  }

  return result
}

/**
 * Компонент с текущим отмеченным адресом и кнопкой сохранения.
 */
export const RouteInfo = forwardRef(function RouteInfo(
  {addresses, setAddresses, onSave, onClose, fullscreen = false}: AddressInfoProps,
  ref,
) {
  const i18n = useI18n()

  const {setValue, watch} = useFormContext()

  const removeHandlers = useRemovePointHandlers({
    addresses,
    setAddresses,
  })

  const [order, setOrder] = useState<string[]>(addresses.map(item => item.id))
  const sortedRouteByOrder = useMemo(
    () => order.map(id => addresses.find(address => address.id === id)).filter(isFilled),
    [order, addresses],
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (result.source.index === result.destination?.index) {
        return
      }

      const newSortOrder = reorder(order, result.source.index, result.destination?.index)
      setOrder(newSortOrder)
      const sortedAddressesByOrder = newSortOrder
        .map(id => addresses.find(address => address.id === id))
        .filter(isFilled)
      setAddresses(sortedAddressesByOrder)

      if (watch().hasOwnProperty('is_route_optimized')) {
        setValue('is_route_optimized', false)
      }
    },
    [order, setAddresses, watch, addresses, setValue],
  )

  return (
    <Paper
      ref={ref}
      className={clsx({[styles.fullscreenRouteInfoContainer]: fullscreen})}
      flexDirection="column"
      float={fullscreen ? 'top' : 'bottom'}
    >
      <Paper item="header" px="m" py="l">
        <div className={styles.header}>
          <Typography thickness="medium" variant="body1">
            {i18n('bdui.route.title', 'Маршрут')}
          </Typography>

          {fullscreen && onClose && (
            <IconButton className={styles.closeButton} onClick={onClose} variant="ghost">
              <Image height={24} src={tagToUrl(CROSS_SLUG)} />
            </IconButton>
          )}
        </div>
      </Paper>
      <Divider />
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={clsx(styles.addressesContainer, {[styles.fullscreenAddressesContainer]: fullscreen})}>
          <Droppable droppableId="drag-route">
            {droppableProvided => (
              <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                {sortedRouteByOrder.map(({address, id}, index) => {
                  const isSource = index === 0

                  if (isSource) {
                    return (
                      <DraggableRouteItem
                        key={id}
                        address={address}
                        index={index}
                        isSource={true}
                        removable={false}
                        removeHandlers={removeHandlers}
                      />
                    )
                  }

                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {draggableProvided => (
                        <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                          <DraggableRouteItem
                            key={id}
                            address={address}
                            draggableProvided={draggableProvided}
                            index={index}
                            isSource={isSource}
                            removable={!isSource && !fullscreen}
                            removeHandlers={removeHandlers}
                          />
                        </div>
                      )}
                    </Draggable>
                  )
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      <Paper className={styles.footer} float={fullscreen ? 'bottom' : undefined} item="footer" pb="s" px="s">
        <Button onPress={onSave} size="l" variant="action" fullWidth>
          {i18n('bdui.route.ready', 'Готово')}
        </Button>
      </Paper>
    </Paper>
  )
})
